import { useEffect } from "react";
import { ErrorBoundary, type FallbackProps } from "react-error-boundary";
import { Outlet, useLocation } from "react-router";
import { Button } from "@/components/ui/button";
import { usePublicReadingText } from "@/hooks/usePublicReadingText";
import {
  applyPublicShareMeta,
  readShareMetaEnv,
  resolvePublicShareMeta,
} from "@/lib/publicShareMeta";

/** WebKit's localized NotFoundError when Chrome Translate moved a text node. */
export const isTranslatedDomCrash = (error: unknown) => {
  if (typeof error !== "object" || error === null) return false;
  const name = "name" in error ? String(error.name) : "";
  const message = "message" in error ? String(error.message) : "";
  return (
    name === "NotFoundError" ||
    /removeChild|insertBefore|not a child|can not be found|no se encuentra/i.test(
      message,
    )
  );
};

const PublicPageError = ({ error }: FallbackProps) => (
  <div
    translate="no"
    className="notranslate flex min-h-[100dvh] flex-col items-center justify-center gap-4 bg-muted px-6 text-center"
  >
    <p className="max-w-sm text-sm text-foreground">
      {isTranslatedDomCrash(error)
        ? "Page translation blocked this screen. Turn off translation in your browser, then reload."
        : "Something went wrong. Reload the page and try again."}
    </p>
    <Button
      type="button"
      variant="primary"
      size="lg"
      onClick={() => window.location.reload()}
    >
      Reload
    </Button>
  </div>
);

const shareEnv = readShareMetaEnv(
  import.meta.env as Record<string, string | undefined>,
);

export const PublicShareLayout = () => {
  const location = useLocation();
  usePublicReadingText();

  useEffect(() => {
    const pageUrl = `${window.location.origin}${location.pathname}${location.search}`;
    const meta = resolvePublicShareMeta(location.pathname, pageUrl, shareEnv);
    applyPublicShareMeta(meta);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!location.pathname.startsWith("/iv")) return;
    const href = "https://js.stripe.com";
    if (document.querySelector(`link[rel="preconnect"][href="${href}"]`)) {
      return;
    }
    const link = document.createElement("link");
    link.rel = "preconnect";
    link.href = href;
    document.head.appendChild(link);
  }, [location.pathname]);

  return (
    <ErrorBoundary
      FallbackComponent={PublicPageError}
      resetKeys={[location.pathname, location.search]}
    >
      <Outlet />
    </ErrorBoundary>
  );
};
